import { useState, useEffect, useCallback, useRef } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useFrontendAuth } from '../hooks/useAuth';
import { useTranslation } from '../context/TranslationContext';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { ValidationError } from '../admin/components/ValidationError';
import ShopCheckbox from '../components/ShopCheckbox';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { checkUsernameAvailable, getUsernameSuggestions } from '../services/api.shop';
import { parseAxiosValidationErrors } from '../utils/productValidation';
import {
    getPersonNameDisallowMessage,
    getUsernameDisallowMessage,
    canSuggestUsernames,
    isDisallowedUsername,
    isUsernameTooShort,
} from '../utils/usernameRules';
import { translateWithFallback } from '../utils/translateWithFallback';
import UsernameSuggestionChips from '../components/UsernameSuggestionChips';

interface RegisterFormData {
    email: string;
    password: string;
    userName: string;
    firstName: string;
    lastName: string;
    confirmPassword: string;
}

type FieldErrors = Record<string, string>;

const Register = () => {
    const navigate = useNavigate();
    const { user, register, loginWithGoogle } = useFrontendAuth();
    const { t, currentLang } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);

    const [formData, setFormData] = useState<RegisterFormData>({
        email: '',
        password: '',
        userName: '',
        firstName: '',
        lastName: '',
        confirmPassword: '',
    });
    const usernameTouchedRef = useRef(false);
    const [suggestionsTaken, setSuggestionsTaken] = useState(false);
    const [suggestionChips, setSuggestionChips] = useState<string[]>([]);
    const wordPairsRef = useRef<string[]>([]);
    const suggestLangRef = useRef(currentLang);
    const [rerolling, setRerolling] = useState(false);
    const suggestGen = useRef(0);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    // Username availability
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [checkingUsername, setCheckingUsername] = useState(false);

    useEffect(() => {
        if (user) navigate('/home', { replace: true });
    }, [user, navigate]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => {
            if (!prev[name] && !prev.General) return prev;
            const next = { ...prev };
            delete next[name];
            delete next.General;
            return next;
        });

        if (name === 'userName') {
            setUsernameAvailable(null);
            usernameTouchedRef.current = true;
        }
    };

    const checkUsernameAvailability = useCallback(async (username: string) => {
        if (!username || isUsernameTooShort(username)) {
            setUsernameAvailable(null);
            return;
        }
        if (isDisallowedUsername(username)) {
            setUsernameAvailable(false);
            return;
        }
        setCheckingUsername(true);
        try {
            const data = await checkUsernameAvailable(username);
            setUsernameAvailable(data.available);
        } catch {
            setUsernameAvailable(null);
        } finally {
            setCheckingUsername(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (formData.userName) checkUsernameAvailability(formData.userName);
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.userName, checkUsernameAvailability]);

    const applyWordPairSuggestions = useCallback((wordPairs: string[], firstNameHandle: string | null, replacePrefill: boolean) => {
        const pairs = wordPairs.filter(Boolean);
        wordPairsRef.current = pairs;
        const chips = [pairs[0], firstNameHandle, pairs[1]].filter((x): x is string => !!x);
        setSuggestionChips(chips);
        setSuggestionsTaken(chips.length === 0);
        if (replacePrefill && pairs[0] && !usernameTouchedRef.current) {
            setFormData((prev) => ({ ...prev, userName: pairs[0] }));
            setUsernameAvailable(true);
        }
    }, []);

    const namesReady = canSuggestUsernames(formData.firstName, formData.lastName);

    useEffect(() => {
        if (!namesReady) {
            suggestGen.current += 1;
            wordPairsRef.current = [];
            setSuggestionChips([]);
            setSuggestionsTaken(false);
            if (!usernameTouchedRef.current) {
                setFormData((prev) => (prev.userName ? { ...prev, userName: '' } : prev));
                setUsernameAvailable(null);
            }
            return;
        }

        const gen = ++suggestGen.current;
        const first = formData.firstName.trim();
        const timer = setTimeout(async () => {
            try {
                const data = await getUsernameSuggestions({
                    firstName: first,
                    language: currentLang,
                });
                if (suggestGen.current !== gen) return;
                const languageChanged = suggestLangRef.current !== currentLang;
                suggestLangRef.current = currentLang;
                const keepPairs = !languageChanged && wordPairsRef.current.length > 0;
                const pairs = keepPairs ? wordPairsRef.current : (data.wordPairs ?? []);
                applyWordPairSuggestions(pairs, data.firstNameHandle, !keepPairs);
            } catch {
                // 429 / network: keep username optional — do not treat as "all taken"
                if (suggestGen.current !== gen) return;
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [namesReady, formData.firstName, currentLang, applyWordPairSuggestions]);

    const handleRerollSuggestions = async () => {
        if (!canSuggestUsernames(formData.firstName, formData.lastName)) return;
        setRerolling(true);
        try {
            const data = await getUsernameSuggestions({
                firstName: formData.firstName.trim(),
                language: currentLang,
                exclude: suggestionChips,
            });
            applyWordPairSuggestions(
                data.wordPairs ?? [],
                data.firstNameHandle,
                !usernameTouchedRef.current
            );
        } catch {
            /* keep current chips */
        } finally {
            setRerolling(false);
        }
    };

    const handlePickChip = (handle: string) => {
        usernameTouchedRef.current = true;
        setFormData((prev) => ({ ...prev, userName: handle }));
        setUsernameAvailable(true);
        setFieldErrors((prev) => {
            if (!prev.userName) return prev;
            const next = { ...prev };
            delete next.userName;
            return next;
        });
    };

    /** Align with Identity rules in Program.cs (digit, upper, lower, non-alphanumeric, length = 8). */
    const passwordMeetsIdentityRules = (password: string) => {
        if (password.length < 8) return false;
        if (!/[A-Z]/.test(password)) return false;
        if (!/[a-z]/.test(password)) return false;
        if (!/\d/.test(password)) return false;
        if (!/[^A-Za-z0-9]/.test(password)) return false;
        return true;
    };

    const getPasswordStrength = (password: string) => {
        if (!password) return { score: 0, label: '', color: '', barColor: '' };
        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;

        const labels = [
            t('page.register.passwordStrength.veryWeak'),
            t('page.register.passwordStrength.weak'),
            t('page.register.passwordStrength.fair'),
            t('page.register.passwordStrength.good'),
            t('page.register.passwordStrength.strong'),
            t('page.register.passwordStrength.excellent'),
        ];
        const colors = ['text-red-500', 'text-orange-500', 'text-yellow-600', 'text-blue-600', 'text-emerald-600', 'text-green-600'];
        const barColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-emerald-500', 'bg-green-600'];

        const idx = Math.min(score, 5);
        return { score: idx, label: labels[idx], color: colors[idx], barColor: barColors[idx] };
    };

    const passwordStrength = getPasswordStrength(formData.password);
    const passwordsMatch = !!formData.confirmPassword && formData.password === formData.confirmPassword;

    const inputClass = (field: string, extra = '') =>
        `w-full rounded-lg border p-3 min-h-11 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] transition-all ${extra} ${
            fieldErrors[field] ? 'border-red-300 bg-red-50/30' : 'border-gray-300'
        }`;

    const mapServerField = (key: string): string => {
        const k = key.replace(/\./g, '').toLowerCase();
        const map: Record<string, string> = {
            firstname: 'firstName',
            lastname: 'lastName',
            username: 'userName',
            email: 'email',
            password: 'password',
            confirmpassword: 'confirmPassword',
            general: 'General',
        };
        return map[k] ?? key;
    };

    const mapRegisterServerErrors = (err: unknown): FieldErrors => {
        const parsed = parseAxiosValidationErrors(err) as Record<string, string>;
        const out: FieldErrors = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (!value) continue;
            out[mapServerField(key)] = value;
        }
        const blob = Object.values(out).join(' ').toLowerCase();
        if (blob.includes('username') && !out.userName) out.userName = Object.values(parsed)[0] || t('page.register.usernameTaken');
        if (blob.includes('first name') && !out.firstName) out.firstName = Object.values(parsed)[0];
        if (blob.includes('last name') && !out.lastName) out.lastName = Object.values(parsed)[0];
        if (blob.includes('email') && !out.email && !out.General) out.email = Object.values(parsed)[0];
        if (Object.keys(out).length === 0) {
            out.General = t('page.register.error');
        }
        return out;
    };

    const validateClient = (): FieldErrors => {
        const errors: FieldErrors = {};
        const firstNameErr = getPersonNameDisallowMessage(
            formData.firstName,
            t('page.register.firstNameLabel')
        );
        if (firstNameErr) errors.firstName = firstNameErr;
        const lastNameErr = getPersonNameDisallowMessage(
            formData.lastName,
            t('page.register.lastNameLabel')
        );
        if (lastNameErr) errors.lastName = lastNameErr;

        if (!formData.email.trim()) {
            errors.email = tr('partner.form.errors.emailRequired', 'Email is required.');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            errors.email = tr('partner.form.errors.emailInvalid', 'Enter a valid email address.');
        }

        if (formData.userName && isUsernameTooShort(formData.userName)) {
            errors.userName =
                getUsernameDisallowMessage(formData.userName) || t('page.register.usernameTooShort');
        } else if (formData.userName && isDisallowedUsername(formData.userName)) {
            errors.userName =
                getUsernameDisallowMessage(formData.userName) || t('page.register.usernameTaken');
        } else if (formData.userName && usernameAvailable === false) {
            errors.userName =
                getUsernameDisallowMessage(formData.userName) || t('page.register.usernameTaken');
        } else if (!formData.userName.trim() && suggestionsTaken) {
            errors.userName = t('page.register.usernameRequiredClash');
        }

        if (!formData.password) {
            errors.password = tr('form.login.errors.passwordRequired', 'Password is required.');
        } else if (!passwordMeetsIdentityRules(formData.password)) {
            errors.password = t('page.register.passwordRequirements');
        }
        if (!formData.confirmPassword) {
            errors.confirmPassword = tr(
                'page.register.errors.confirmRequired',
                'Confirm your password.'
            );
        } else if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = t('page.register.passwordMismatch');
        }
        if (!acceptedTerms) {
            errors.terms = t('page.register.mustAcceptTerms');
        }
        return errors;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const clientErrors = validateClient();
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            scrollToFirstErrorField(clientErrors);
            return;
        }

        setFieldErrors({});
        setLoading(true);

        try {
            await register(
                formData.email.trim(),
                formData.userName.trim() || '',
                formData.password,
                currentLang,
                formData.firstName.trim(),
                formData.lastName.trim()
            );
        } catch (err: unknown) {
            const next = mapRegisterServerErrors(err);
            setFieldErrors(next);
            scrollToFirstErrorField(next);
        } finally {
            setLoading(false);
        }
    };

    const EyeIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    );

    const EyeOffIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
    );

    return (
        <div className="container mx-auto max-w-md px-4 py-8 sm:py-12">
            <div className="mb-8 text-center">
                <h1 className="mb-2 text-2xl font-bold sm:text-3xl">{t('page.register.title')}</h1>
                <p className="text-gray-600">{t('page.register.subtitle')}</p>
                <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                    {t('auth.tagline') === 'auth.tagline'
                        ? "Belgium's marketplace for pre-loved baby gear"
                        : t('auth.tagline')}
                </p>
            </div>

            <form noValidate onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="firstName">
                        {t('page.register.firstNameLabel')}
                    </label>
                    <input
                        id="firstName"
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder={t('page.register.firstNamePlaceholder')}
                        className={inputClass('firstName')}
                        autoComplete="given-name"
                    />
                    <ValidationError error={fieldErrors.firstName} />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="lastName">
                        {t('page.register.lastNameLabel')}
                    </label>
                    <input
                        id="lastName"
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder={t('page.register.lastNamePlaceholder')}
                        className={inputClass('lastName')}
                        autoComplete="family-name"
                    />
                    <ValidationError error={fieldErrors.lastName} />
                </div>
                {/* Email field */}
                <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="email">
                        {t('page.register.emailLabel')}
                    </label>
                    <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('page.register.emailPlaceholder')}
                        className={inputClass('email')}
                        autoComplete="email"
                    />
                    <ValidationError error={fieldErrors.email} />
                    <p className="mt-1 text-xs text-gray-500">
                        {t('page.register.emailHint')}
                    </p>
                    <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                        {t('auth.privacy.nudge') === 'auth.privacy.nudge'
                            ? 'We never share your email with other parents or for spam.'
                            : t('auth.privacy.nudge')}
                    </p>
                </div>

                {/* Username with live check */}
                <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="userName">
                        {t('page.register.usernameLabel')}
                    </label>
                    <input
                        id="userName"
                        type="text"
                        name="userName"
                        value={formData.userName}
                        onChange={handleChange}
                        onBlur={() => {
                            if (!formData.userName.trim() && suggestionChips[0]) {
                                usernameTouchedRef.current = false;
                                setFormData((prev) => ({ ...prev, userName: suggestionChips[0] }));
                                setUsernameAvailable(true);
                            }
                        }}
                        placeholder={
                            namesReady && suggestionChips[0]
                                ? `@${suggestionChips[0]}`
                                : t('page.register.usernamePlaceholder')
                        }
                        className={inputClass('userName')}
                        autoComplete="username"
                    />
                    <ValidationError error={fieldErrors.userName} />
                    <p className="mt-1 text-xs text-gray-500">
                        {namesReady
                            ? tr(
                                  'page.register.usernameHelp',
                                  'This is how other parents see you. You can leave it or pick another.'
                              )
                            : tr(
                                  'page.register.usernameHelpPending',
                                  'We will suggest a public name after your first and last name. You can also type your own.'
                              )}
                    </p>
                    {namesReady && (
                        <UsernameSuggestionChips
                            chips={suggestionChips}
                            selected={formData.userName}
                            onSelect={handlePickChip}
                            onReroll={handleRerollSuggestions}
                            rerolling={rerolling}
                        />
                    )}
                    {suggestionsTaken && !formData.userName && (
                        <p className="mt-1 text-xs text-red-500">
                            {t('page.register.usernameRequiredClash')}
                        </p>
                    )}
                    {formData.userName && (
                        <div className="mt-1 text-xs">
                            {isUsernameTooShort(formData.userName)
                                ? (
                                    <span className="text-red-500">
                                        {'\u2717'} {t('page.register.usernameTooShort')}
                                    </span>
                                  )
                                : checkingUsername
                                ? t('page.register.usernameChecking')
                                : usernameAvailable === true
                                  ? (
                                      <span className="text-emerald-600">
                                        {'\u2713'} {t('page.register.usernameAvailable')}
                                      </span>
                                    )
                                  : usernameAvailable === false
                                    ? (
                                        <span className="text-red-500">
                                          {'\u2717'}{' '}
                                          {getUsernameDisallowMessage(formData.userName) ||
                                              t('page.register.usernameTaken')}
                                        </span>
                                      )
                                    : null}
                        </div>
                    )}
                </div>

                {/* Password + Strength */}
                <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="password">
                        {t('page.register.passwordLabel')}
                    </label>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                            className={inputClass('password', 'pr-12')}
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? EyeOffIcon : EyeIcon}
                        </button>
                    </div>

                    {formData.password && (
                        <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">
                                    {t('page.register.passwordStrength.label')}
                                </span>
                                <span className={`font-medium ${passwordStrength.color}`}>{passwordStrength.label}</span>
                            </div>
                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full transition-all ${passwordStrength.barColor}`} style={{ width: `${(passwordStrength.score / 5) * 100}%` }} />
                            </div>
                            {!passwordMeetsIdentityRules(formData.password) && !fieldErrors.password && (
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('page.register.passwordRequirements')}
                                </p>
                            )}
                        </div>
                    )}
                    <ValidationError error={fieldErrors.password} />
                </div>

                {/* Confirm Password */}
                <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="confirmPassword">
                        {t('page.register.confirmPasswordLabel')}
                    </label>
                    <div className="relative">
                        <input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                            className={inputClass('confirmPassword', 'pr-12')}
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                            {showConfirmPassword ? EyeOffIcon : EyeIcon}
                        </button>
                    </div>

                    {formData.confirmPassword && !fieldErrors.confirmPassword && (
                        <div className={`mt-1 text-xs ${passwordsMatch ? 'text-emerald-600' : 'text-red-500'}`}>
                            {passwordsMatch
                                ? `\u2713 ${t('page.register.passwordsMatch')}`
                                : `\u2717 ${t('page.register.passwordsDoNotMatch')}`}
                        </div>
                    )}
                    <ValidationError error={fieldErrors.confirmPassword} />
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3" data-error-field="terms">
                    <ShopCheckbox
                        id="terms"
                        name="terms"
                        checked={acceptedTerms}
                        onChange={(v) => {
                            setAcceptedTerms(v);
                            setFieldErrors((prev) => {
                                if (!prev.terms) return prev;
                                const next = { ...prev };
                                delete next.terms;
                                return next;
                            });
                        }}
                        className="mt-0.5"
                        aria-label="Accept terms"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600">
                        {t('page.register.termsAgree')}{' '}
                        <Link
                            to="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--accent-color)] hover:underline"
                        >
                            {t('page.register.termsOfService')}
                        </Link>
                        {' '}{t('page.register.termsAnd')}{' '}
                        <Link
                            to="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--accent-color)] hover:underline"
                        >
                            {t('page.register.privacyPolicy')}
                        </Link>
                        .
                    </label>
                </div>
                <ValidationError error={fieldErrors.terms} />

                <ValidationError error={fieldErrors.General} />

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[var(--accent-color)] text-white py-3 px-4 rounded-lg font-medium disabled:opacity-60 hover:opacity-90 transition-all flex items-center justify-center gap-2"
                >
                    {loading ? (
                        <>
                            <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                            {t('page.register.creating')}
                        </>
                    ) : (
                        t('page.register.submit')
                    )}
                </button>
            </form>

            <GoogleSignInButton
                onCredential={async (idToken) => {
                    setLoading(true);
                    setFieldErrors({});
                    try {
                        await loginWithGoogle(idToken, currentLang);
                        navigate('/home', { replace: true });
                    } catch (err: unknown) {
                        const next: FieldErrors =
                            err instanceof AxiosError && err.response?.status === 429
                                ? { General: t('error.toomanyattempts') }
                                : err instanceof AxiosError &&
                                    err.response?.data?.error === 'account_blocked'
                                  ? {}
                                  : {
                                        General: tr(
                                            'error.googleSignIn',
                                            'Google sign-in failed. Please try again.'
                                        ),
                                    };
                        if (
                            err instanceof AxiosError &&
                            err.response?.data?.error === 'account_blocked'
                        ) {
                            navigate('/account-status', {
                                state: {
                                    status: err.response.data.status,
                                    message: err.response.data.message,
                                },
                            });
                        }
                        setFieldErrors(next);
                        scrollToFirstErrorField(next);
                    } finally {
                        setLoading(false);
                    }
                }}
                onError={(msg) => setFieldErrors({ General: msg })}
                disabled={loading}
            />

            <div className="mt-8 text-center text-sm text-gray-600">
                {t('page.register.alreadyHaveAccount')}{' '}
                <Link to="/login" className="font-medium text-[var(--accent-color)] hover:underline">{t('page.register.signIn')}</Link>
            </div>
        </div>
    );
};

export default Register;
